import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Upload, AlertTriangle, CheckCircle, Database, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDataMigration = () => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await adminFetch(`${API}/admin/export-data`);
      
      if (!response.ok) throw new Error('Error al exportar');
      
      const data = await response.json();
      setExportData(data);
      
      // Download as file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jon-quintano-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Datos exportados correctamente');
    } catch (error) {
      toast.error('Error al exportar datos');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.collections) {
        throw new Error('Formato de archivo inválido');
      }
      
      const response = await adminFetch(`${API}/admin/import-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Error al importar');
      }
      
      const result = await response.json();
      setImportResult(result);
      toast.success(`Importados ${result.total} documentos`);
    } catch (error) {
      toast.error(error.message || 'Error al importar datos');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-8">
          <h1 className="font-serif text-3xl mb-2">Migración de Datos</h1>
          <p className="text-muted-foreground">
            Exporta o importa los datos entre entornos (desarrollo ↔ producción)
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Export Section */}
          <div className="bg-card border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500/10 flex items-center justify-center">
                <Download className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h2 className="font-medium">Exportar Datos</h2>
                <p className="text-sm text-muted-foreground">Descarga todos los datos como JSON</p>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Exporta galerías, posts del blog, servicios, clientes, testimonios y más.
              Las imágenes se mantienen en Cloudinary (solo se exportan las URLs).
            </p>
            
            <Button 
              onClick={handleExport} 
              disabled={exporting}
              className="w-full gap-2"
              data-testid="export-btn"
            >
              {exporting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Exportar Datos
                </>
              )}
            </Button>

            {exportData && (
              <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 text-sm">
                <div className="flex items-center gap-2 text-green-500 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Exportación completada</span>
                </div>
                <p className="text-muted-foreground">
                  {Object.values(exportData.collections).reduce((a, b) => a + b.length, 0)} documentos exportados
                </p>
              </div>
            )}
          </div>

          {/* Import Section */}
          <div className="bg-card border border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-500/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h2 className="font-medium">Importar Datos</h2>
                <p className="text-sm text-muted-foreground">Carga datos desde un archivo JSON</p>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 p-3 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-200">
                  <strong>Atención:</strong> Esta operación reemplazará TODOS los datos existentes. 
                  Asegúrate de tener una copia de seguridad.
                </p>
              </div>
            </div>
            
            <label className="block">
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                disabled={importing}
                className="hidden"
                data-testid="import-input"
              />
              <Button 
                variant="outline" 
                disabled={importing}
                className="w-full gap-2 cursor-pointer"
                asChild
              >
                <span>
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Seleccionar archivo JSON
                    </>
                  )}
                </span>
              </Button>
            </label>

            {importResult && (
              <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 text-sm">
                <div className="flex items-center gap-2 text-green-500 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Importación completada</span>
                </div>
                <p className="text-muted-foreground mb-2">
                  Total: {importResult.total} documentos importados
                </p>
                <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                  {Object.entries(importResult.imported).map(([col, count]) => (
                    <div key={col} className="flex justify-between">
                      <span>{col}:</span>
                      <span>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-card border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-medium">Instrucciones de Migración</h2>
          </div>
          
          <div className="space-y-4 text-sm text-muted-foreground">
            <div>
              <h3 className="font-medium text-foreground mb-1">Para migrar de Desarrollo → Producción:</h3>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>En <strong>desarrollo</strong>, haz clic en "Exportar Datos" para descargar el archivo JSON</li>
                <li>Haz Deploy de la aplicación a producción</li>
                <li>En <strong>producción</strong>, ve a esta misma página y usa "Importar Datos"</li>
                <li>Selecciona el archivo JSON que descargaste</li>
              </ol>
            </div>
            
            <div>
              <h3 className="font-medium text-foreground mb-1">Notas importantes:</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Las imágenes están en Cloudinary y son accesibles desde ambos entornos</li>
                <li>La importación <strong>reemplaza</strong> todos los datos existentes</li>
                <li>Se recomienda exportar primero en producción como backup antes de importar</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDataMigration;
